import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLazyQuery } from '@apollo/client/react';
import { TextField, Box, Button } from 'components/ui';
import { WideForm } from 'components/styles_components/form';
import SpaceNameAvailableQuery from 'queries/space/name_available';
import validationSchema from 'validation/space/create';

const DEBOUNCE_MS = 500;
const MIN_NAME_LENGTH = 3;

const FormBody = ({ formik, processing, onCancel, originalName }) => {
  const intl = useIntl();
  const name = formik.values.name?.trim() ?? '';
  const unchanged = originalName !== undefined && name === originalName.trim();
  const [pending, setPending] = useState(false);

  const [checkAvailability, { data, loading }] = useLazyQuery(SpaceNameAvailableQuery, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (name.length < MIN_NAME_LENGTH || unchanged) {
      setPending(false);
      return;
    }
    setPending(true);
    const handle = setTimeout(() => {
      checkAvailability({ variables: { name } });
      setPending(false);
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [name, unchanged, checkAvailability]);

  const checking = !unchanged && (pending || loading);
  const taken =
    !unchanged &&
    !checking &&
    name.length >= MIN_NAME_LENGTH &&
    data?.spaceNameAvailable === false;

  const yupErrorVisible = formik.touched.name && Boolean(formik.errors.name);
  const yupHelper = formik.touched.name && formik.errors.name;
  const helperText =
    yupHelper ||
    (taken ? intl.formatMessage({ id: 'validation.space.name.taken' }) : '') ||
    (checking ? intl.formatMessage({ id: 'validation.space.name.checking' }) : '');

  return (
    <WideForm onSubmit={formik.handleSubmit}>
      <TextField
        autoFocus
        fullWidth
        margin="dense"
        id="name"
        name="name"
        label={intl.formatMessage({ id: 'space.name' })}
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={yupErrorVisible || taken}
        helperText={helperText}
      />
      <Button
        className="margin-top-10"
        fullWidth
        disabled={!formik.isValid || processing || !formik.dirty || taken || checking}
        color="primary"
        variant="contained"
        type="submit"
      >
        <FormattedMessage id="button.save" />
      </Button>
      <Box display="flex" justifyContent="flex-end">
        <Button variant="text" size="small" disabled={processing} onClick={onCancel}>
          <FormattedMessage id="button.cancel" />
        </Button>
      </Box>
    </WideForm>
  );
};

const Form = ({ initialValues, processing, onSubmit, onCancel, originalName }) => {
  const intl = useIntl();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(intl)}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <FormBody
          formik={formik}
          processing={processing}
          onCancel={onCancel}
          originalName={originalName}
        />
      )}
    </Formik>
  );
};

export default Form;
